import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// --- Icon Imports ---
import {
    Trophy, User, LogOut, Home, Lock, Search, Loader, X, ChevronRight, BookOpen, AlertTriangle, Video, Play, MessageSquare, Menu
} from 'lucide-react';
// --- Swiper.js Imports ---
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Navigation } from 'swiper/modules';
// --- Swiper.js Styles ---
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { generateAvatar } from '../../api/user.api';
import { useCharacter } from '../../context/CharacterContext';
import StoryViewer from '../character/StoryViewer';

// Tutorial data will now be fetched from API
// Main Dashboard Component

const CyberAegisDashboard = ({ apiService, userProfile, onLogout }) => {
    // --- State and Handlers (Unchanged) ---
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [userProfileState, setUserProfileState] = useState(userProfile);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { showStoryViewer, showStoryViewerModal, hideStoryViewerModal } = useCharacter();
    const [showTutorial, setShowTutorial] = useState(null);
    const [tutorialStep, setTutorialStep] = useState(0);
    const [tutorialData, setTutorialData] = useState(null);
    const [tutorialLoading, setTutorialLoading] = useState(false);
    // Track which arena tutorials are completed for this user
    const [completedTutorials, setCompletedTutorials] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const swiperRef = useRef(null);
    const [showMFABanner, setShowMFABanner] = useState(false);
    const [showMobileNav, setShowMobileNav] = useState(false);

    const learningArenas = useMemo(() => [

        { id: 'phishing_social_engineering', title: 'Phishing & Social Engineering', imageUrl: '/assets/images/Challenges/phishing.png', difficulty: 'Beginner', isUnlocked: true, prerequisite: null },
        { id: 'password_security', title: 'Password Security', imageUrl: '/assets/images/Challenges/password.png', difficulty: 'Beginner', isUnlocked: true, prerequisite: 'Phishing & Social Engineering' },
        // { id: 'malware_ransomware', title: 'Malware & Ransomware', imageUrl: '/assets/images/Challenges/malware.png', difficulty: 'Advance', isUnlocked: true, prerequisite: 'Password Security & Auth' },
        // { id: 'network_security', title: 'Network Security', imageUrl: '/assets/images/Challenges/network.png', difficulty: 'Advance', isUnlocked: true, prerequisite: 'Malware & Ransomware' },
        { id: 'data_protection', title: 'Data Protection', imageUrl: '/assets/images/Challenges/data_protection.png', difficulty: 'Intermediate', isUnlocked: true, prerequisite: 'Network Security' },
        // { id: 'incident_response', title: 'Incident Response', imageUrl: '/assets/images/Challenges/incident_response.png', difficulty: 'Intermediate', isUnlocked: true, prerequisite: 'Data Protection' }
    ], []);

    const filteredArenas = useMemo(() => {
        if (!searchQuery) return learningArenas;
        return learningArenas.filter(arena =>
            arena.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery, learningArenas]);

    useEffect(() => {
        if (swiperRef.current && swiperRef.current.swiper) {
            swiperRef.current.swiper.slideTo(0); // Go to the first slide of the filtered results
            swiperRef.current.swiper.update();
        }
    }, [filteredArenas]);

    const handleLogout = () => setShowLogoutConfirm(true);
    const confirmLogout = () => { onLogout(); setShowLogoutConfirm(false); };

    // Function to fetch tutorial data from API
    const fetchTutorialData = async (arenaId) => {
        try {
            setTutorialLoading(true);
            const response = await apiService.get(`/tutorials/${arenaId}`);
            const completed = !!response?.data?.userProgress?.completed;
            setCompletedTutorials(prev => ({ ...prev, [arenaId]: completed }));
            if (response.success && response.data && response.data.steps && response.data.steps.length > 0) {
                // Transform API data to match expected format
                const transformedData = {
                    [arenaId]: response.data.steps.map(step => ({
                        image: step.image,
                        title: step.title,
                        text: step.description
                    }))
                };

                // console.log('Transformed tutorial data:', transformedData); // Debug log
                setTutorialData(transformedData);
                return { hasTutorial: true, completed };
            }

            // console.log('No tutorial data found for arena:', arenaId); // Debug log
            return { hasTutorial: false, completed };
        } catch (error) {
            console.error('Error fetching tutorial data:', error);
            return { hasTutorial: false, completed: false };
        } finally {
            setTutorialLoading(false);
        }
    };


    const handleEnterArena = useCallback(async (arena) => {
        // console.log('handleEnterArena called for:', arena.id); // Debug log
        if (!arena.isUnlocked) return;

        // Check subscription plan - free users can only access demo arena
        // Try multiple paths to get plan ID
        const userPlanId = userProfile?.currentPlanId ||
            userProfile?.current_plan_id ||
            userProfileState?.currentPlanId ||
            userProfileState?.current_plan_id ||
            userProfile?.data?.current_plan_id;

        if (userPlanId === 'free') {
            alert('Your current plan (Free) only allows access to the Demo Arena. Please upgrade your subscription to access full arenas.');
            navigate('/demo-arena');
            return;
        }

        // Check if user has subscription features for this arena
        const subscriptionFeatures = userProfile?.subscription?.features ||
            userProfileState?.subscription?.features ||
            userProfile?.data?.subscription?.features;

        if (subscriptionFeatures && subscriptionFeatures.subscription_levels) {
            const allowedArenas = Object.keys(subscriptionFeatures.subscription_levels);
            // Map arena IDs to subscription_levels keys (some may have different names)
            const arenaMapping = {
                'phishing_social_engineering': 'phishing_social_engineering',
                'password_security': 'password_security',
                'data_protection': 'data_protection',
                'network_security': 'network_security',
                'incident_response': 'incident_response',
                'malware_ransomware': 'malware_ransomware'
            };

            const mappedArenaId = arenaMapping[arena.id];
            if (mappedArenaId && !allowedArenas.includes(mappedArenaId)) {
                alert(`This arena is not included in your current subscription plan. Please upgrade to access "${arena.title}".`);
                return;
            }
        }

        // If user already completed tutorial, go straight to challenges
        if (completedTutorials[arena.id]) {
            // console.log('Tutorial already completed for:', arena.id, 'navigating to challenges');
            navigate(`/challenges?arena=${arena.id}`);
            return;
        }


        // Check if we already have tutorial data for this arena (not yet completed)
        if (tutorialData && tutorialData[arena.id]) {
            // console.log('Using existing tutorial data for:', arena.id); // Debug log
            setShowTutorial(arena.id);
            setTutorialStep(0);
            return;
        }


        const { hasTutorial, completed } = await fetchTutorialData(arena.id);

        if (completed) {
            // console.log('Tutorial previously completed, navigating to challenges for:', arena.id);
            navigate(`/challenges?arena=${arena.id}`);
            return;
        }

        if (hasTutorial) {
            // console.log('Tutorial found, showing modal for:', arena.id); // Debug log
            setShowTutorial(arena.id);
            setTutorialStep(0);
        } else {
            // console.log('No tutorial found, navigating to challenges for:', arena.id); // Debug log
            navigate(`/challenges?arena=${arena.id}`);
        }
    }, [navigate, tutorialData, completedTutorials]);

    const handleTutorialNext = () => setTutorialStep(prev => prev + 1);
    const handleTutorialFinish = async () => {

        // Mark tutorial as completed in the database

        if (showTutorial) {
            try {
                await apiService.post(`/tutorials/${showTutorial}/complete`);
                console.log('Tutorial marked as completed');
                setCompletedTutorials(prev => ({ ...prev, [showTutorial]: true }));
            } catch (error) {
                console.error('Error marking tutorial as completed:', error);
            }
        }

        navigate(`/challenges?arena=${showTutorial}`);
        setShowTutorial(null);

    };

    const handleTutorialClose = () => {
        setShowTutorial(null);
        navigate('/dashboard');
    };


    // Handler for "Want to Learn" button - navigates to learning module
    const handleWantToLearn = () => {
        if (showTutorial) {
            navigate(`/learning/module/${showTutorial}?topic=${showTutorial}`);
            setShowTutorial(null);
        }
    };


    // Handle URL parameters for tutorial navigation
    useEffect(() => {
        const handleTutorialFromUrl = async () => {
            const searchParams = new URLSearchParams(location.search);
            const tutorialParam = searchParams.get('tutorial');
            const stepParam = searchParams.get('step');

            if (tutorialParam) {
                // Fetch data if it's not already loaded
                if (!tutorialData || !tutorialData[tutorialParam]) {
                    await fetchTutorialData(tutorialParam);
                }
                // Now tutorialData should be populated, so we can show the modal
                setShowTutorial(tutorialParam);
                const step = stepParam ? parseInt(stepParam, 10) : 0;
                setTutorialStep(step);
            }
        };

        handleTutorialFromUrl();
    }, [location.search]); // This effect runs when the URL query string changes


    // Reset search query on component unmount
    useEffect(() => {
        return () => {
            setSearchQuery('');
        };
    }, []);


    // Check MFA status and show banner if not enabled
    useEffect(() => {
        const checkMFAStatus = async () => {
            if (userProfileState?.id) {
                try {
                    const token = localStorage.getItem('authToken');
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/mfa/status`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        setShowMFABanner(!data.mfaEnabled);
                    }
                } catch (error) {
                    // console.log('Could not check MFA status:', error);
                }
            }
        };

        checkMFAStatus();
    }, [userProfileState?.id]);


    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setAvatarLoading(true);
        try {
            const data = await generateAvatar(file);
            if (!data.success) throw new Error(data.error || 'Failed to generate avatar');
            setUserProfileState(prev => ({ ...prev, avatar_url: data.avatar_url }));
        } catch (error) { console.error(error); }
        finally {
            setAvatarLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div
            className="relative font-sans text-white min-h-screen flex overflow-x-hidden bg-cover bg-center"
            style={{ backgroundImage: `url('/assets/images/challenges.gif')` }}
        >
            {/* Background Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#071942]/80 to-[#3c2d0a]/50 z-0" />

            {/* --- Left Sidebar Navigation --- */}
            <aside className="relative z-10 hidden md:flex w-24 bg-black/30 p-4 flex-col items-center justify-between border-r border-white/10">
                <div>
                    <div className="mb-12 cursor-pointer" onClick={() => navigate('/dashboard')}>
                        <img
                            src={'/assets/images/logo.png'}
                            alt="Cyberjugs logo"
                            className="w-16 h-16 cursor-pointer hover:border-blue-500 transition-all"
                        />
                    </div>
                    <nav className="flex flex-col items-center gap-8">
                        <button onClick={() => navigate('/dashboard')} title="Home" className="p-3 bg-white/5 rounded-lg hover:bg-blue-600 transition-colors"><Home size={24} /></button>
                        <button onClick={() => navigate('/leaderboard')} title="Leaderboard" className="p-3 bg-white/5 rounded-lg hover:bg-blue-600 transition-colors"><Trophy size={24} /></button>
                        <button onClick={() => navigate('/community')} title="Community Forum" className="p-3 bg-white/5 rounded-lg hover:bg-blue-600 transition-colors"><MessageSquare size={24} /></button>
                        <button onClick={() => navigate('/demo-arena')} title="Demo Arena" className="p-3 bg-white/5 rounded-lg hover:bg-blue-600 transition-colors"><Play size={24} /></button>
                        <button onClick={showStoryViewerModal} title="View Story" className="p-3 bg-white/5 rounded-lg hover:bg-blue-600 transition-colors"><Video size={24} /></button>
                        <button onClick={() => navigate('/profile')} title="Profile" className="p-3 bg-white/5 rounded-lg hover:bg-blue-600 transition-colors"><User size={24} /></button>
                    </nav>
                </div>
                <div className="flex flex-col items-center gap-6">
                    {/* Avatar */}
                    <div className="relative">
                        <img
                            src={userProfileState?.avatar_url || userProfileState?.data?.avatar_url || userProfile?.avatar_url || userProfile?.data?.avatar_url || '/assets/images/avatar.png'}
                            alt="User Avatar"
                            className="w-12 h-12 rounded-full cursor-pointer border-2 border-gray-600 hover:border-blue-500 transition-all object-cover"
                            onClick={() => navigate('/my-profile')}
                        />
                        {avatarLoading && <Loader className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-white" />}
                    </div>

                    {/* Username Below Avatar */}
                    <div className="text-center">
                        <p className="text-xs font-bold text-white tracking-wider">
                            {(() => {
                                const name =
                                    userProfileState?.data?.first_name ||
                                    userProfileState?.first_name ||
                                    userProfile?.data?.first_name ||
                                    userProfile?.first_name;
                                return name || '';
                            })()}
                        </p>
                    </div>

                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
                    <button onClick={handleLogout} title="Logout" className="p-3 bg-white/5 rounded-lg hover:bg-red-600 transition-colors">
                        <LogOut size={24} />
                    </button>
                </div>
            </aside>

            {/* --- Main Content Area --- */}
            <main className="relative z-10 flex-1 flex flex-col p-4 md:p-6 lg:p-8 overflow-y-auto">
                <div className="md:hidden mb-4">
                    <div className="flex items-center justify-between gap-3 rounded-xl bg-black/30 border border-white/10 p-3">
                        <button
                            onClick={() => setShowMobileNav((prev) => !prev)}
                            className="p-2 rounded-lg bg-white/10 hover:bg-blue-600 transition-colors"
                            aria-label="Toggle navigation menu"
                        >
                            <Menu size={20} />
                        </button>
                        <img
                            src={'/assets/images/logo.png'}
                            alt="Cyberjugs logo"
                            className="w-10 h-10"
                        />
                        <button
                            onClick={handleLogout}
                            className="p-2 rounded-lg bg-white/10 hover:bg-red-600 transition-colors"
                            aria-label="Logout"
                        >
                            <LogOut size={18} />
                        </button>
                    </div>
                    {showMobileNav && (
                        <div className="mt-2 grid grid-cols-3 gap-2 rounded-xl bg-black/40 border border-white/10 p-2">
                            <button onClick={() => { navigate('/dashboard'); setShowMobileNav(false); }} className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors"><Home size={18} className="mx-auto" /></button>
                            <button onClick={() => { navigate('/leaderboard'); setShowMobileNav(false); }} className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors"><Trophy size={18} className="mx-auto" /></button>
                            <button onClick={() => { navigate('/community'); setShowMobileNav(false); }} className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors"><MessageSquare size={18} className="mx-auto" /></button>
                            <button onClick={() => { navigate('/demo-arena'); setShowMobileNav(false); }} className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors"><Play size={18} className="mx-auto" /></button>
                            <button onClick={() => { showStoryViewerModal(); setShowMobileNav(false); }} className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors"><Video size={18} className="mx-auto" /></button>
                            <button onClick={() => { navigate('/profile'); setShowMobileNav(false); }} className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors"><User size={18} className="mx-auto" /></button>
                        </div>
                    )}
                </div>
                <header className="flex-shrink-0 mb-8">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <h1 className="text-2xl md:text-3xl font-bold" style={{ textShadow: '0 0 5px rgba(255, 242, 0, 1), 0 0 10px rgba(0, 132, 255, 1), 0 0 20px rgba(212, 0, 255, 1)' }}>Cyber Jugs</h1>
                        <div className="w-full md:flex-1 md:max-w-lg relative md:mx-4">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search modules or challenges..."
                                className="w-full bg-white/10 border border-white/20 rounded-full py-2.5 pl-12 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)} />
                        </div>
                    </div>
                </header>

                {/* MFA Security Banner */}
                {showMFABanner && (
                    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 z-50 md:max-w-sm w-auto md:w-full bg-yellow-900/50 border border-yellow-700 rounded-lg p-4 shadow-lg backdrop-blur-sm">
                        <div className="flex items-start space-x-3">
                            <AlertTriangle className="w-6 h-6 text-yellow-400 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                                <h3 className="font-semibold text-yellow-200 mb-1">Security Recommendation</h3>
                                <p className="text-sm text-yellow-300 mb-4">
                                    Enable two-factor authentication (MFA) to secure your account with an extra layer of protection.
                                </p>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => navigate('/mfa-setup')}
                                        className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Enable MFA Now
                                    </button>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowMFABanner(false)}
                                className="p-1 text-yellow-300 hover:text-white rounded-full hover:bg-white/10"
                                aria-label="Dismiss"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                )}

                <div className="flex-1 flex flex-col justify-center items-center w-full">
                    <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 " style={{ textShadow: '0 0 5px rgba(255, 242, 0, 1), 0 0 10px rgba(0, 132, 255, 1), 0 0 20px rgba(212, 0, 255, 1)' }}>Cyber Gaming Arenas</h2>
                    <p className="text-gray-300 mb-10 text-center">Select an arena to begin your training.</p>
                    <Swiper
                        ref={swiperRef}
                        effect={'coverflow'} grabCursor={true} centeredSlides={true}
                        slidesPerView={'auto'} navigation={true} pagination={{ clickable: true }}
                        coverflowEffect={{ rotate: 50, stretch: 0, depth: 100, modifier: 1, slideShadows: true }}
                        modules={[EffectCoverflow, Pagination, Navigation]}
                        className="w-full max-w-6xl h-[390px] md:h-[450px]"
                    >
                        {filteredArenas.map((arena, index) => (
                            <SwiperSlide key={arena.id} style={{ width: 'min(80vw, 350px)', height: 'min(72vw, 420px)' }}>
                                <div className={`relative h-full w-full rounded-2xl overflow-hidden group transition-all duration-300 ${arena.isUnlocked && index === 0 ? 'cursor-pointer hover:scale-105' : 'opacity-60 cursor-not-allowed'}`} onClick={() => index === 0 && handleEnterArena(arena)}>
                                    {/* <div className={`relative h-full w-full rounded-2xl overflow-hidden group transition-all duration-300 ${arena.isUnlocked ? 'cursor-pointer hover:scale-105' : 'opacity-60 cursor-not-allowed'}`} onClick={() => handleEnterArena(arena)}> */}
                                    <img src={arena.imageUrl} alt={arena.title} className="w-full h-full object-cover scale-105 object-contain bg-black brightness-110 group-hover:brightness-150 transition-all duration-300" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent p-6 flex flex-col justify-end">
                                        <h4 className="font-bold text-xl text-center text-gray-200" style={{ textShadow: '0 0 1px rgba(255, 242, 0, 1), 0 0 10px rgba(0, 132, 255, 1), 0 0 20px rgba(212, 0, 255, 1)' }}>{arena.title}</h4>
                                        {/* <p className="text-sm text-gray-300 mt-1">{arena.difficulty}</p> */}
                                    </div>
                                    {index > 0 && (
                                        <div className="absolute top-4 left-4 bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg transform -rotate-12">
                                            Coming Soon
                                        </div>
                                    )}
                                    {!arena.isUnlocked && index > 0 && (
                                        <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-center p-4 backdrop-blur-sm">
                                            <Lock size={32} className="text-gray-400 mb-3" />
                                            <p className="text-gray-200">Complete the prerequisite to unlock:</p>
                                            <p className="font-semibold text-blue-400 block mt-1">{arena.prerequisite}</p>
                                        </div>
                                    )}
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </main>

            {/* --- Tutorial Modal --- */}
            {showTutorial && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-lg flex items-center justify-center z-50 p-4 md:p-8">
                    {tutorialLoading ? (
                        <div className="flex items-center justify-center h-96">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
                        </div>
                    ) : tutorialData && tutorialData[showTutorial] ? (
                        <>
                            <div className="relative w-full h-full max-w-7xl rounded-2xl overflow-hidden shadow-2xl shadow-blue-900/50 border border-blue-500/30">
                                <img
                                    src={tutorialData[showTutorial][tutorialStep].image}
                                    alt="Tutorial background"
                                    className="absolute inset-0 w-full h-full object-contain bg-gray-900"
                                    onError={(e) => {
                                        console.warn('Tutorial image failed to load:', e.target.src);
                                        e.target.src = '/assets/images/characters/Raghav1.png';
                                    }}
                                />
                                <button onClick={handleTutorialClose} className="absolute top-4 right-4 z-20 p-2 bg-black/50 rounded-full text-gray-300 hover:text-white hover:bg-black/80 transition-all">
                                    <X size={24} />
                                </button>

                                <div className={`absolute top-1/2 -translate-y-1/2 h-auto max-h-[85%] w-11/12 md:w-[35%] p-6 md:p-8 bg-black/50 backdrop-blur-md rounded-lg border border-white/10 transition-all duration-500 ease-in-out ${tutorialStep % 2 === 0 ? 'right-4 md:right-8' : 'left-4 md:left-8'
                                    }`}>
                                    <h3 className="text-2xl md:text-3xl font-bold text-blue-300 mb-4">{tutorialData[showTutorial][tutorialStep].title}</h3>
                                    <p className="text-gray-300 text-sm md:text-base">{tutorialData[showTutorial][tutorialStep].text}</p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center text-white">
                            <p>No tutorial available for this arena.</p>
                            <button onClick={handleTutorialClose} className="mt-4 px-4 py-2 bg-blue-600 rounded-lg">
                                Close
                            </button>
                        </div>
                    )}

                    {showTutorial && tutorialData && tutorialData[showTutorial] && tutorialStep === tutorialData[showTutorial].length - 1 && (
                        <div className="fixed bottom-4 left-4 md:bottom-8 md:left-8 z-50">
                            <button
                                onClick={handleWantToLearn}
                                className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-purple-600/80 backdrop-blur-sm border border-purple-400/50 flex items-center justify-center text-white font-bold text-[10px] md:text-sm hover:bg-purple-600 hover:shadow-[0_0_10px_#f0f,0_0_25px_#f0f] transition-all duration-300"
                                title="Want to Learn"
                            >
                                <BookOpen size={18} className="mr-1 md:w-6 md:h-6" />
                                <span className="text-xs">LEARN</span>
                            </button>
                        </div>
                    )}

                    {tutorialData && tutorialData[showTutorial] && (
                        <div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 z-50">
                            {tutorialStep < tutorialData[showTutorial].length - 1 ? (
                                <button
                                    onClick={handleTutorialNext}
                                    className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-blue-600/80 backdrop-blur-sm border border-blue-400/50 flex items-center justify-center text-white hover:bg-blue-600 hover:shadow-[0_0_10px_#0ff,0_0_25px_#0ff] transition-all duration-300"
                                    title="Next Step"
                                >
                                    <ChevronRight size={22} className="md:w-8 md:h-8" />
                                </button>
                            ) : (
                                <button
                                    onClick={handleTutorialFinish}
                                    className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-green-600/80 backdrop-blur-sm border border-green-400/50 flex items-center justify-center text-white font-bold text-[10px] md:text-sm hover:bg-green-600 hover:shadow-[0_0_10px_#0f0,0_0_25px_#0f0] transition-all duration-300"
                                    title="Start Challenge"
                                >
                                    START
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* --- Logout Modal --- */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4 border border-gray-700">
                        <h3 className="text-lg font-semibold mb-4">Confirm Logout</h3>
                        <p className="text-gray-300 mb-6">Are you sure you want to log out?</p>
                        <div className="flex space-x-4">
                            <button onClick={() => setShowLogoutConfirm(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg font-medium transition-colors">Cancel</button>
                            <button onClick={confirmLogout} className="flex-1 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg font-medium transition-colors">Logout</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Story Viewer Modal */}
            <StoryViewer
                isOpen={showStoryViewer}
                onClose={hideStoryViewerModal}
            />
        </div>
    );
};

export default CyberAegisDashboard;